<template lang="pug">
ValidationObserver(
  ref="dialogObserver"
  v-slot="{ valid }"
  slim
)
  AppDialog.wow(
    :class="{ opened: show, 'put-under': clientDialogOpen, edit: editing }"
    :title="`${editing ? 'Edit' : 'New'} work order`"
    show
    append-to-body
    @close="closeWizard"
  )
    form#work-order-form(@submit.prevent="onFormSubmit")
      AppWizard(
        :value="step"
        :editing="editing"
        :steps="workOrderSteps()"
        @next="nextStep"
        @previous="previousStep"
        @tab-selected="changeTab"
      )
        AppWizardStep
          .hidden(slot="footer")
          ValidationObserver(
            ref="createStep"
            vid="createObs"
            slim
          )
            CreateStep(
              v-show="step === 0"
              :form="form"
              :editing="editing"
              :select2-options="select2Options"
              :grouped-clients="groupedClients"
              @toggle-client-dialog="clientDialogOpen = $event"
            )
        AppWizardStep
          .hidden(slot="footer")
          ValidationObserver(
            ref="siteStep"
            vid="siteObs"
            slim
          )
            WorkOrderSiteStep(
              v-show="step === 1"
              :form="form"
              :editing="editing"
              :visible="step === 1"
              :readonly="isImmutableJobSite"
            )

        template(v-if="editing && canManageBudget")
          AppWizardStep
            .hidden(slot="footer")
            ValidationObserver(
              ref="budgetStep"
              vid="budgetObs"
              slim
            )
              WorkOrdersBudgetStep(
                v-show="step === 2"
                :form="form"
                :organization="organization"
                :duration-format="durationFormat"
                :has-budget="!!editingWorkOrder.has_budget"
                @reset-budget="onBudgetStateReset"
                @update-budget="onBudgetStateUpdate"
              )

    template(slot="footer")
      template(v-if="!editing")
        AppButton.btn-default.pull-left(@click="closeWizard") Cancel
        div(v-if="step === 0")
          button.btn.hs-btn-transparent-primary.save-exit-link(
            type="submit"
            form="work-order-form"
            :disabled="!valid || form.processing"
            @click="saveAction = 'save-exit'"
          ) Save and close
          AppButton(
            type="primary"
            :disabled="!valid"
            @click="nextStep"
          ) Next
        div(v-else)
          AppButton(
            type="default"
            @click="previousStep"
          ) Back
          button.btn.btn-primary.total-save-link(
            type="submit"
            form="work-order-form"
            :disabled="!valid || form.processing"
            @click="saveAction = 'save-total'"
          ) Save
      template(v-else)
        .d-flex.justify-content-between.flex-1
          AppButton(
            type="default"
            @click="closeWizard"
          ) Cancel
          button.btn.btn-primary(
            type="submit"
            form="work-order-form"
            :disabled="!valid || form.processing"
            @click="saveAction = 'save-total'"
          ) Save
</template>

<script>
import { get } from 'lodash-es';
import CreateStep from './CreateStep.vue';
import { mapInertiaProps } from '~/utils/inertia';
import { JOB_SITE_ACTIONS, WORK_ORDERS_URL } from '~/app_constants';
import WorkOrdersBudgetStep from './WorkOrdersBudgetStep.vue';
import WorkOrderSiteStep from '../jobs_dialog/WorkOrderSiteStep.vue';
import DialogToasterMixin from '~/mixins/WorkOrders/DialogToasterMixin';
import { prepareBudgetPayload, prepareJobSitePayload } from '~/stores/WorkOrderStore/common/utils';
import { applyTemplate } from '~/utils/url_template';

export default {
  name: 'WorkOrdersDialog',
  components: {
    CreateStep,
    WorkOrderSiteStep,
    WorkOrdersBudgetStep,
  },
  mixins: [
    DialogToasterMixin({
      updateMessage: 'Work order successfully updated',
      createMessage: 'Work order successfully created',
    }),
  ],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    show: {
      type: Boolean,
      default: false,
    },
    defaultTab: {
      type: Number,
      default: 0,
    },
    editingWorkOrder: {
      type: Object,
      default: () => ({}),
    },
    groupedClients: {
      type: Array,
      required: true,
    },
    canManageBudget: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    const jobSite = get(this.editingWorkOrder, 'jobSite', null);
    const budget = this.editing ? this.editingWorkOrder.budget : null;

    return {
      step: 0,
      saveAction: null,
      budgetState: {},
      clientDialogOpen: false,
      hasResetBudget: false,
      form: this.$inertia.form({
        id: this.editing ? this.editingWorkOrder.id : null,
        // Create Step
        number: this.editing ? this.editingWorkOrder.number : null,
        client_id: this.editing ? this.editingWorkOrder.client.id : null,
        instructions: this.editing ? this.editingWorkOrder.instructions : null,
        // Site Step
        radius: get(jobSite, 'radius', null),
        address: get(jobSite, 'address', null),
        position: {
          lat: get(jobSite, 'position.lat', null),
          lng: get(jobSite, 'position.lng', null),
        },
        enter_action: get(jobSite, 'enter_action', JOB_SITE_ACTIONS.doNothing),
        exit_action: get(jobSite, 'exit_action', JOB_SITE_ACTIONS.doNothing),
        ...// Budget Step
        // We only care about the budget in editing mode
        (!this.editing
          ? {}
          : {
              type: budget.type,
              rate: budget.rate,
              alert: budget.alert,
              stop_timer: budget.stop_timer,
              cost: budget.cost || null,
              recurrence: budget.recurrence,
              include_non_billable: budget.include_non_billable,
              start: budget.start ? moment(budget.start) : null,
              hours_display: budget.hours ? budget.hours * 60 * 60 : null,
            }),
      }),
    };
  },
  computed: {
    ...mapInertiaProps({
      clients: 'clients',
      organization: 'organization',
      jobSiteMinimumRadius: 'job_site_minimum_radius',
      jobSiteDefaultRadius: 'job_site_default_radius',
      durationFormat: ({ current_user: user }) => user.duration_format,
    }),
    select2Options() {
      return {
        width: '100%',
      };
    },
    isImmutableJobSite() {
      return this.editing && this.editingWorkOrder && this.editingWorkOrder.job_site_immutable;
    },
  },
  watch: {
    async 'form.client_id'(newValue) {
      if (newValue && newValue !== 'add-client' && !this.editing) {
        const client = this.clients.find((client) => client.id === newValue);

        if (client) {
          this.form.address = client.address;
        }
      }
    },
  },
  mounted() {
    this.form.radius = this.form.radius || this.jobSiteDefaultRadius || this.jobSiteMinimumRadius;

    if (typeof this.defaultTab === 'number') {
      this.step = this.defaultTab;
    }
  },
  methods: {
    workOrderSteps() {
      const steps = [
        { title: this.editing ? 'General' : 'Create', id: 'createStep' },
        { title: 'Job site', id: 'siteStep' },
        ...(this.editing && this.canManageBudget ? [{ title: 'Budget', id: 'budgetStep' }] : []),
      ];

      return steps.map((step) => {
        const observer = get(this.$refs, step.id);
        const hasError = !!(observer && observer.flags.dirty && observer.flags.invalid);

        return {
          ...step,
          hasError,
        };
      });
    },
    closeWizard(id = null) {
      this.$emit('close', id);
    },

    /**
     * Wizard steps navigation
     */
    changeTab(tabId) {
      const id = Number(tabId.replace('tab-', ''));

      this.step = id;
    },
    nextStep() {
      this.step++;
    },
    previousStep() {
      this.step--;
    },

    /**
     * Form handling
     */
    onBudgetStateUpdate() {
      this.hasResetBudget = false;
    },
    onBudgetStateReset() {
      this.hasResetBudget = true;
    },
    onFormSubmit() {
      const { origin } = window.location;
      const url = new URL(
        applyTemplate(`${WORK_ORDERS_URL}`, {
          organizationId: this.organization.id,
          woId: this.editing ? this.editingWorkOrder.id : null,
        }),
        origin,
      );
      const workOrdersUrl = url.toString();
      const action = this.saveAction;

      this.form
        // This will mutate the underlying form data that we're
        // going to send to the server.
        .transform((data) => {
          const { client_id: clientId, number, instructions } = data;
          const payload = { number, instructions, client_id: clientId };

          if (action === 'save-total') {
            // Job site payload
            prepareJobSitePayload(payload, data);

            if (this.editing) {
              // Add the id for the work order
              payload.id = this.editingWorkOrder.id;
              // Budget payload
              prepareBudgetPayload(payload, this.hasResetBudget, data);
            }
          }

          return { work_order: payload };
        });

      const options = { onSuccess: () => this.closeWizard() };

      if (this.editing) {
        // Check if the user has reset the budget and if so,
        // make sure that it was the intended action.
        // If not, just cancel the update.
        if (
          this.hasResetBudget &&
          !confirm("Removing this work order's budget will remove its budget history. Are you sure?")
        ) {
          return;
        }

        return this.form.put(workOrdersUrl, options);
      }

      return this.form.post(workOrdersUrl, options);
    },
  },
};
</script>
